/**
 * Stable visitor-feed avatar gradients.
 * Hash is deterministic per seed so re-renders never flicker a new palette.
 *
 * Cool soft mesh: periwinkle / sky / muted teal / soft violet —
 * airy pastels with film grain (not letter monograms, not brand orange).
 */

type MeshPalette = {
  /** Soft radial blob A */
  a: string
  /** Soft radial blob B */
  b: string
  /** Linear base — from */
  from: string
  /** Linear base — to */
  to: string
  angle: number
}

/** Cool-leaning pastels — low chroma, high lightness (frosted SaaS). */
const VISITOR_MESHES: MeshPalette[] = [
  // Periwinkle → sky (primary reference)
  {
    a: 'oklch(0.88 0.055 275 / 0.95)',
    b: 'oklch(0.84 0.09 230 / 0.9)',
    from: 'oklch(0.86 0.06 268)',
    to: 'oklch(0.80 0.10 220)',
    angle: 105,
  },
  // Soft sky → ice
  {
    a: 'oklch(0.90 0.05 230 / 0.9)',
    b: 'oklch(0.85 0.07 205 / 0.85)',
    from: 'oklch(0.88 0.055 225)',
    to: 'oklch(0.82 0.08 200)',
    angle: 120,
  },
  // Muted teal mist
  {
    a: 'oklch(0.87 0.06 195 / 0.9)',
    b: 'oklch(0.82 0.07 175 / 0.85)',
    from: 'oklch(0.85 0.055 200)',
    to: 'oklch(0.78 0.07 170)',
    angle: 135,
  },
  // Soft violet → periwinkle
  {
    a: 'oklch(0.88 0.05 295 / 0.9)',
    b: 'oklch(0.84 0.07 270 / 0.85)',
    from: 'oklch(0.86 0.05 290)',
    to: 'oklch(0.80 0.08 255)',
    angle: 115,
  },
  // Cool slate-blue
  {
    a: 'oklch(0.86 0.04 250 / 0.9)',
    b: 'oklch(0.80 0.06 235 / 0.85)',
    from: 'oklch(0.84 0.045 245)',
    to: 'oklch(0.76 0.07 220)',
    angle: 140,
  },
  // Aqua frost
  {
    a: 'oklch(0.89 0.055 210 / 0.9)',
    b: 'oklch(0.83 0.07 185 / 0.85)',
    from: 'oklch(0.87 0.05 215)',
    to: 'oklch(0.79 0.08 180)',
    angle: 100,
  },
  // Dusty periwinkle
  {
    a: 'oklch(0.87 0.05 280 / 0.92)',
    b: 'oklch(0.82 0.06 245 / 0.88)',
    from: 'oklch(0.85 0.05 275)',
    to: 'oklch(0.78 0.07 240)',
    angle: 125,
  },
  // Soft cyan-lilac cross
  {
    a: 'oklch(0.88 0.06 220 / 0.9)',
    b: 'oklch(0.84 0.055 285 / 0.85)',
    from: 'oklch(0.86 0.05 235)',
    to: 'oklch(0.80 0.07 270)',
    angle: 150,
  },
  // Pale denim
  {
    a: 'oklch(0.86 0.055 255 / 0.9)',
    b: 'oklch(0.81 0.08 225 / 0.85)',
    from: 'oklch(0.84 0.06 250)',
    to: 'oklch(0.77 0.09 215)',
    angle: 110,
  },
  // Misty teal-violet
  {
    a: 'oklch(0.87 0.05 185 / 0.9)',
    b: 'oklch(0.83 0.055 280 / 0.85)',
    from: 'oklch(0.85 0.045 200)',
    to: 'oklch(0.78 0.06 265)',
    angle: 130,
  },
  // Airy cornflower
  {
    a: 'oklch(0.89 0.06 260 / 0.92)',
    b: 'oklch(0.84 0.08 235 / 0.88)',
    from: 'oklch(0.87 0.055 255)',
    to: 'oklch(0.80 0.09 225)',
    angle: 95,
  },
  // Cool glacier
  {
    a: 'oklch(0.90 0.04 220 / 0.9)',
    b: 'oklch(0.85 0.06 200 / 0.85)',
    from: 'oklch(0.88 0.04 230)',
    to: 'oklch(0.80 0.07 195)',
    angle: 145,
  },
]

/**
 * Tiny fractal-noise tile for film grain.
 * Applied as an overlay — keep opacity low for performance (CSS only, no runtime filter).
 */
export const VISITOR_AVATAR_GRAIN =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 128 128' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0.55 0 0 0 0 0.6 0 0 0 0 0.7 0 0 0 0.55 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"

/** FNV-1a 32-bit — fast, stable, no deps. */
function hashSeed(input: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** Build a stable seed from visitor/event identity. */
export function visitorAvatarSeed(parts: {
  id?: string
  country?: string
  region?: string
}): string {
  const country = parts.country?.trim() || ''
  const region = parts.region?.trim() || ''
  const id = parts.id?.trim() || ''
  // Prefer geo identity so the same place stays visually consistent;
  // fold id so distinct events still get variety when geo is missing/shared.
  if (country || region) return `${country}|${region}|${id}`
  return id || 'unknown'
}

/**
 * Layered CSS `background-image` value — soft mesh (2 radials + linear).
 * Pair with {@link VISITOR_AVATAR_GRAIN} overlay in the avatar DOM for film grain.
 */
export function getVisitorAvatarGradient(seed: string): string {
  const h = hashSeed(seed)
  const mesh = VISITOR_MESHES[h % VISITOR_MESHES.length]!
  const angle = mesh.angle + ((h >>> 8) % 41) - 20
  const ax = 20 + ((h >>> 12) % 45)
  const ay = 15 + ((h >>> 18) % 40)
  const bx = 55 + ((h >>> 22) % 40)
  const by = 50 + ((h >>> 26) % 40)

  return [
    `radial-gradient(circle at ${ax}% ${ay}%, ${mesh.a}, transparent 58%)`,
    `radial-gradient(circle at ${bx}% ${by}%, ${mesh.b}, transparent 55%)`,
    `linear-gradient(${angle}deg, ${mesh.from}, ${mesh.to})`,
  ].join(', ')
}

/**
 * ISO 3166-1 alpha-2 → regional-indicator flag emoji.
 * Returns empty string when code is missing/invalid (caller can omit overlay).
 */
export function getCountryFlagEmoji(code?: string | null): string {
  if (!code || code === 'World' || code === 'Unknown') return ''
  const upper = code.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(upper)) return ''
  return String.fromCodePoint(
    ...[...upper].map((ch) => 0x1f1e6 - 65 + ch.charCodeAt(0)),
  )
}

/**
 * Hue trios for the visitor orb (live feed, visitor profile). Neighbouring
 * hues so each orb reads as one blurred colour field, not a rainbow.
 */
const ORB_HUES: Array<[number, number, number]> = [
  [25, 50, 330], // coral, amber, rose
  [15, 40, 300], // red, orange, magenta
  [290, 260, 330], // violet, indigo, pink
  [255, 220, 300], // blue, sky, violet
  [160, 130, 230], // green, lime, blue
  [90, 60, 25], // yellow, amber, coral
  [200, 170, 250], // cyan, teal, periwinkle
  [340, 10, 280], // pink, red, purple
]

/**
 * Stable blurred-gradient orb for a visitor key: three soft colour blobs over
 * a base, like a defocused photo. Same key, same orb everywhere the visitor
 * appears. Returns a CSS `background-image` value.
 */
export function getVisitorOrbGradient(key: string): string {
  const h = hashSeed(key || 'unknown')
  const [h1, h2, h3] = ORB_HUES[h % ORB_HUES.length]!
  const spin = (h >>> 6) % 360
  const at = (shift: number) => {
    const angle = ((spin + shift) * Math.PI) / 180
    return `${Math.round(50 + 30 * Math.cos(angle))}% ${Math.round(50 + 30 * Math.sin(angle))}%`
  }
  return [
    `radial-gradient(circle at 30% 25%, oklch(1 0 0 / 0.35), transparent 45%)`,
    `radial-gradient(circle at ${at(0)}, oklch(0.72 0.19 ${h1}), transparent 62%)`,
    `radial-gradient(circle at ${at(130)}, oklch(0.8 0.16 ${h2}), transparent 62%)`,
    `radial-gradient(circle at ${at(250)}, oklch(0.62 0.2 ${h3}), transparent 62%)`,
    `linear-gradient(${spin}deg, oklch(0.74 0.15 ${h1}), oklch(0.66 0.17 ${h3}))`,
  ].join(', ')
}
